import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { endpoints } from "@/config/endpoints";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Trash2,
  Plus,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/auth.service";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";

interface Product {
  _id: string;
  title: string;
  type: "building_material" | "tool";
  types: string[];
  description?: string;
  quantity?: string;
  numberOfItems?: string;
}

const TOOL_CATEGORIES = {
  HEAVY_MACHINERY: [
    "Bobcat machine",
    "Mixer machine",
    "Batching Plant",
    "Dumping level",
    "JCB",
    "Water pump",
    "South Conc Pump",
    "Concrete Pump SP-1200",
  ],
  CONCRETE_EQUIPMENT: [
    "Vibrator Poker",
    "Cube moulds",
    "Casting rod",
    "Gauge boxes",
    "Copping moulds",
    "Window seal mould",
    "Coupling moulds",
    "Moulding cube",
  ],
  CONCRETE_PUMP_PARTS: [
    "Conc Pump Pipe (1m, 2m, 3m)",
    "Conc Pump Main Pipe",
    "Conc Pump Pipe 90° Bend",
    "Conc Pump Pipe 45° Bend",
    "Conc Pump Pipe 60° Bend",
    "Reducing Pipe",
    "Conc Pump Clamps",
    "Conc Pump Main Clamps",
    "Conc pump Ball",
    "Red Concrete Pipes",
    "Clamp Rubbers",
  ],
  SCAFFOLDING: [
    "Scaffolds",
    "Makasi",
    "Cuplock (various sizes: 1.2m, 2m, 2.5m)",
    "Cuplock Ledger (1.2m, 2.4m)",
    "Cuplock Bracket",
    "Round Pipe (various sizes: 1m, 1.2m, 1.4m, 2m, 2.5m, 3m, 6m)",
    "Scaffolding Clamps",
    "Connectors",
    "Screw Jack (Big, Small, L-shape, Big U, Small U)",
    "Flat base",
    "Scaffolding stands",
    "Universal clamps",
    "Metal Trappers (various sizes: 600x120, 300x120, 200x120)",
    "Light gauge prop (4m)",
    "Heavy gauge prop (4m)",
  ],
  SUPPORT_BEAMS: [
    "Support Beam (1.2m, 1.8m, 2.4m, 3.6m, 4.2m)",
    "Slab Beam",
    "Aluminium Beam",
    "H-beam (5m)",
    "Column support beam",
  ],
  FORMWORK: [
    "Column box (300x300)",
    "Tie rods",
    "Push and pull drops",
    "Light gauge drops",
  ],
  HAND_TOOLS: [
    "Spade",
    "Mattock",
    "Jembe",
    "Crowbars",
    "Kibui",
    "Modified Kibui",
    "Panga (including damaged)",
    "Shokas (axes)",
    "Mason Hammer",
    "Mallet",
    "Sururu",
    "Slashers",
    "Shovels",
  ],
  MEASURING_TOOLS: ["Tape measure"],
  TRANSPORT: ["Wheelbarrow"],
  STORAGE: ["Metal water tank", "Plastic Tank", "Metallic Tanks"],
  FASTENERS: ["Nuts & Bolts"],
  SAFETY: [
    "First aid kit",
    "Helmets",
    "Reflector jackets",
    "Gumboots",
    "Safety Belts",
  ],
  FURNITURE: ["Plastic Chairs", "Table"],
  MISCELLANEOUS: ["Hose Pipe", "Solar Lights", "Spanner"],
};

const BUILDING_MATERIALS = {
  SAND: ["River Sand", "Rock Sand"],
  BALLAST: ["Ballast"],
  CEMENT: ["Cement 42.5", "Cement 32.5"],
  REINFORCEMENT: ["D8", "D10", "D12", "D16", "D20", "D25", "D32"],
  MARINE_PLY: ["Marine Ply"],
  GASOLINE: ["Diesel", "Petro"],
  STONES: [
    'Machine Cut - 4"',
    'Machine Cut - 6"',
    'Machine Cut - 8"',
    "Natural Stone",
  ],
  BINDING_WIRE: ["Binding Wire"],
  NAILS: ["Nails"],
  HOLLOW_POTS: ["Hollow Pots"],
  TIMBER: ["Timber"],
};

const Products = () => {
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [updateForm, setUpdateForm] = useState<Partial<Product>>({});
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    title: "",
    type: "building_material",
    types: [],
    description: "",
    quantity: "",
    numberOfItems: "",
  });
  const [currentType, setCurrentType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  const { token } = authService.getStoredAuth();

  const {
    data: products,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await fetch(`${endpoints.products.list}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(`${endpoints.products.list}`, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to fetch products after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to fetch products");
      }
      return response.json();
    },
  });

  // Sort products by type (building materials first)
  const sortedProducts = products?.sort((a, b) => {
    if (a.type === "building_material" && b.type !== "building_material")
      return -1;
    if (a.type !== "building_material" && b.type === "building_material")
      return 1;
    return 0;
  });

  // Pagination logic
  const totalPages = sortedProducts
    ? Math.ceil(sortedProducts.length / itemsPerPage)
    : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = sortedProducts?.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const createProductMutation = useMutation({
    mutationFn: async (product: Partial<Product>) => {
      const response = await fetch(`${endpoints.products.list}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(product),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            // Try to refresh the token
            const newToken = await authService.refreshToken();
            // Retry the request with new token
            const retryResponse = await fetch(`${endpoints.products.list}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              body: JSON.stringify(product),
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to create product after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            // If refresh fails, logout
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to create product");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product created successfully");
      setIsAddProductModalOpen(false);
      setNewProduct({
        title: "",
        type: "building_material",
        types: [],
        description: "",
        quantity: "",
        numberOfItems: "",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create product");
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: async (updatedProduct: Product) => {
      const response = await fetch(
        `${endpoints.products.product(updatedProduct._id)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedProduct),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          try {
            // Try to refresh the token
            const newToken = await authService.refreshToken();
            // Retry the request with new token
            const retryResponse = await fetch(
              `${endpoints.products.product(updatedProduct._id)}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${newToken}`,
                },
                body: JSON.stringify(updatedProduct),
              }
            );
            if (!retryResponse.ok) {
              throw new Error("Failed to update product after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            // If refresh fails, logout
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to update product");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product updated successfully");
      setSelectedProduct(null);
      setUpdateForm({});
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update product");
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (productIds: string | string[]) => {
      const { token } = authService.getStoredAuth();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const isBulkDelete = Array.isArray(productIds);
      const endpoint = isBulkDelete
        ? endpoints.products.bulkDelete
        : endpoints.products.product(productIds as string);

      const response = await fetch(endpoint, {
        method: isBulkDelete ? "POST" : "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        ...(isBulkDelete && { body: JSON.stringify({ productIds }) }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(endpoint, {
              method: isBulkDelete ? "POST" : "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              ...(isBulkDelete && { body: JSON.stringify({ productIds }) }),
            });
            if (!retryResponse.ok) {
              throw new Error(
                "Failed to delete product(s) after token refresh"
              );
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to delete product(s)");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setSelectedProducts([]);
      toast.success("Product(s) deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete product(s)");
    },
  });

  const handleDelete = (productId: string) => {
    deleteProductMutation.mutate(productId);
  };

  const handleBulkDelete = () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select products to delete");
      return;
    }
    deleteProductMutation.mutate(selectedProducts);
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === currentProducts?.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(currentProducts?.map((product) => product._id) || []);
    }
  };

  const handleUpdate = (product: Product) => {
    setSelectedProduct(product);
    setUpdateForm(product);
  };

  const handleUpdateSubmit = () => {
    if (selectedProduct) {
      updateProductMutation.mutate({ ...selectedProduct, ...updateForm });
    }
  };

  const handleCreateSubmit = () => {
    createProductMutation.mutate(newProduct);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardHeader></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Types</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[150px]" />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Skeleton className="h-8 w-8" />
                          <Skeleton className="h-8 w-8" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardHeader>
              <CardTitle>Products</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center items-center h-64 text-red-500">
                Error loading products
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Product List</CardTitle>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search products..."
                    className="pl-8 w-[300px]"
                  />
                </div>
                <Button
                  onClick={() => setIsAddProductModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Product
                </Button>
                {selectedProducts.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Selected ({selectedProducts.length})
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={
                          selectedProducts.length === currentProducts?.length
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Product Type</TableHead>
                    <TableHead>Types</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    [...Array(5)].map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton className="h-4 w-[50px]" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-[200px]" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-[100px]" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-[300px]" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-[100px]" />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Skeleton className="h-8 w-8" />
                            <Skeleton className="h-8 w-8" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <div className="text-destructive">
                          Error loading products. Please try again.
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : currentProducts?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <div className="text-muted-foreground">
                          No products found
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentProducts?.map((product) => (
                      <TableRow key={product._id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedProducts.includes(product._id)}
                            onCheckedChange={() =>
                              handleSelectProduct(product._id)
                            }
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {product.title}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              product.type === "building_material"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }
                          >
                            {product.type === "building_material"
                              ? "Building Material"
                              : "Tool"}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-md">
                          {product.type === "building_material" ? (
                            <div className="flex flex-wrap gap-1">
                              {product.types?.map((type, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                                >
                                  {type}
                                </Badge>
                              ))}
                              {(!product.types ||
                                product.types.length === 0) && (
                                <span className="text-muted-foreground text-sm">
                                  No types
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              N/A
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {product.type === "tool" ? (
                            <div className="space-y-1">
                              {product.quantity && (
                                <div className="flex items-center">
                                  <span className="text-sm font-medium mr-1">
                                    Quantity:
                                  </span>
                                  <span className="text-sm">
                                    {product.quantity}
                                  </span>
                                </div>
                              )}
                              {product.numberOfItems && (
                                <div className="flex items-center">
                                  <span className="text-sm font-medium mr-1">
                                    Items:
                                  </span>
                                  <span className="text-sm">
                                    {product.numberOfItems}
                                  </span>
                                </div>
                              )}
                              {!product.quantity && !product.numberOfItems && (
                                <span className="text-muted-foreground text-sm">
                                  No details
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              N/A
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleUpdate(product)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="icon">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Are you sure?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This action cannot be undone. This will
                                    permanently delete the product.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(product._id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          {!isLoading && !error && products?.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="flex items-center space-x-2">
                <p className="text-sm text-muted-foreground">Items per page:</p>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={handleItemsPerPageChange}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={itemsPerPage} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <SelectItem key={pageSize} value={pageSize.toString()}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Add Product Dialog */}
        <Dialog
          open={isAddProductModalOpen}
          onOpenChange={setIsAddProductModalOpen}
        >
          <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
              <DialogDescription>
                Fill in the details to create a new product
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="product-type">Product Type</Label>
                <Select
                  value={newProduct.type}
                  onValueChange={(value) => {
                    setNewProduct({
                      ...newProduct,
                      type: value as "building_material" | "tool",
                      types: [],
                      description: "",
                      quantity: "",
                      numberOfItems: "",
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="building_material">
                      Building Material
                    </SelectItem>
                    <SelectItem value="tool">Tool</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newProduct.type === "building_material" && (
                <div className="space-y-2">
                  <Label htmlFor="building-material-category">
                    Building Material Category
                  </Label>
                  <Select
                    value={currentType}
                    onValueChange={(value) => {
                      setCurrentType(value);
                      setNewProduct({
                        ...newProduct,
                        title: value
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                          )
                          .join(" "),
                        types: [],
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select building material category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(BUILDING_MATERIALS).map(
                        ([category, items]) => (
                          <SelectItem key={category} value={category}>
                            {category
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )
                              .join(" ")}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {newProduct.type === "building_material" && currentType && (
                <div className="space-y-2">
                  <Label htmlFor="building-material-type">Material Type</Label>
                  <Select
                    value={newProduct.types[0] || ""}
                    onValueChange={(value) => {
                      setNewProduct({
                        ...newProduct,
                        types: [value],
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select material type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUILDING_MATERIALS[
                        currentType as keyof typeof BUILDING_MATERIALS
                      ]?.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {newProduct.type === "building_material" ? (
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newProduct.description}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter product description"
                    className="min-h-[100px]"
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="tool-category">Tool Category</Label>
                    <Select
                      value={currentType}
                      onValueChange={(value) => {
                        setCurrentType(value);
                        setNewProduct({
                          ...newProduct,
                          types: [value],
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select tool category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TOOL_CATEGORIES).map(
                          ([category, items]) => (
                            <SelectItem key={category} value={category}>
                              {category
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase()
                                )
                                .join(" ")}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  {currentType && (
                    <div className="space-y-2">
                      <Label htmlFor="tool-type">Tool Type</Label>
                      <Select
                        value={newProduct.types[0] || ""}
                        onValueChange={(value) => {
                          setNewProduct({
                            ...newProduct,
                            types: [value],
                            title: value,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tool type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TOOL_CATEGORIES[
                            currentType as keyof typeof TOOL_CATEGORIES
                          ]?.map((tool) => (
                            <SelectItem key={tool} value={tool}>
                              {tool}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="numberOfItems">Number of Items</Label>
                    <Input
                      id="numberOfItems"
                      value={newProduct.numberOfItems || ""}
                      onChange={(e) =>
                        setNewProduct({
                          ...newProduct,
                          numberOfItems: e.target.value,
                        })
                      }
                      placeholder="Enter number of items"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <div className="space-y-2">
                      <Textarea
                        id="description"
                        value={newProduct.description}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            description: e.target.value,
                          })
                        }
                        placeholder="Enter product description"
                        className="min-h-[100px]"
                      />
                      <p className="text-sm text-muted-foreground">
                        Include details like:
                        <ul className="list-disc list-inside mt-1">
                          <li>Condition of the tool</li>
                          <li>Any specific measurements or specifications</li>
                          <li>Additional accessories included</li>
                          <li>Any maintenance requirements</li>
                        </ul>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddProductModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateSubmit}
                className="bg-primary hover:bg-primary/90"
              >
                Create Product
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Product Dialog */}
        {selectedProduct && (
          <Dialog
            open={!!selectedProduct}
            onOpenChange={() => setSelectedProduct(null)}
          >
            <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Product</DialogTitle>
                <DialogDescription>
                  Make changes to the product details
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-type">Product Type</Label>
                  <Select
                    value={updateForm.type || selectedProduct.type}
                    onValueChange={(value) => {
                      setUpdateForm({
                        ...updateForm,
                        type: value as "building_material" | "tool",
                        types: [],
                        description: "",
                        quantity: "",
                        numberOfItems: "",
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="building_material">
                        Building Material
                      </SelectItem>
                      <SelectItem value="tool">Tool</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(updateForm.type || selectedProduct.type) ===
                  "building_material" && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-building-material-category">
                      Building Material Category
                    </Label>
                    <Select
                      value={currentType}
                      onValueChange={(value) => {
                        setCurrentType(value);
                        setUpdateForm({
                          ...updateForm,
                          title: value
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1).toLowerCase()
                            )
                            .join(" "),
                          types: [],
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select building material category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(BUILDING_MATERIALS).map(
                          ([category, items]) => (
                            <SelectItem key={category} value={category}>
                              {category
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase()
                                )
                                .join(" ")}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {(updateForm.type || selectedProduct.type) ===
                  "building_material" &&
                  currentType && (
                    <div className="space-y-2">
                      <Label htmlFor="edit-building-material-type">
                        Material Type
                      </Label>
                      <Select
                        value={
                          updateForm.types?.[0] ||
                          selectedProduct.types[0] ||
                          ""
                        }
                        onValueChange={(value) => {
                          setUpdateForm({
                            ...updateForm,
                            types: [value],
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select material type" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUILDING_MATERIALS[
                            currentType as keyof typeof BUILDING_MATERIALS
                          ]?.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                {(updateForm.type || selectedProduct.type) ===
                "building_material" ? (
                  <div className="space-y-2">
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea
                      id="edit-description"
                      value={
                        updateForm.description || selectedProduct.description
                      }
                      onChange={(e) =>
                        setUpdateForm({
                          ...updateForm,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter product description"
                      className="min-h-[100px]"
                    />
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="edit-tool-category">Tool Category</Label>
                      <Select
                        value={currentType}
                        onValueChange={(value) => {
                          setCurrentType(value);
                          setUpdateForm({
                            ...updateForm,
                            types: [value],
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tool category" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(TOOL_CATEGORIES).map(
                            ([category, items]) => (
                              <SelectItem key={category} value={category}>
                                {category
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1).toLowerCase()
                                  )
                                  .join(" ")}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    {currentType && (
                      <div className="space-y-2">
                        <Label htmlFor="edit-tool-type">Tool Type</Label>
                        <Select
                          value={
                            updateForm.types?.[0] ||
                            selectedProduct.types[0] ||
                            ""
                          }
                          onValueChange={(value) => {
                            setUpdateForm({
                              ...updateForm,
                              types: [value],
                              title: value,
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select tool type" />
                          </SelectTrigger>
                          <SelectContent>
                            {TOOL_CATEGORIES[
                              currentType as keyof typeof TOOL_CATEGORIES
                            ]?.map((tool) => (
                              <SelectItem key={tool} value={tool}>
                                {tool}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="edit-numberOfItems">
                        Number of Items
                      </Label>
                      <Input
                        id="edit-numberOfItems"
                        value={
                          updateForm.numberOfItems ||
                          selectedProduct.numberOfItems ||
                          ""
                        }
                        onChange={(e) =>
                          setUpdateForm({
                            ...updateForm,
                            numberOfItems: e.target.value,
                          })
                        }
                        placeholder="Enter number of items"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-description">Description</Label>
                      <div className="space-y-2">
                        <Textarea
                          id="edit-description"
                          value={
                            updateForm.description ||
                            selectedProduct.description
                          }
                          onChange={(e) =>
                            setUpdateForm({
                              ...updateForm,
                              description: e.target.value,
                            })
                          }
                          placeholder="Enter product description"
                          className="min-h-[100px]"
                        />
                        <p className="text-sm text-muted-foreground">
                          Include details like:
                          <ul className="list-disc list-inside mt-1">
                            <li>Condition of the tool</li>
                            <li>Any specific measurements or specifications</li>
                            <li>Additional accessories included</li>
                            <li>Any maintenance requirements</li>
                          </ul>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSelectedProduct(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateSubmit}
                  className="bg-primary hover:bg-primary/90"
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Products;
